import dayjs from 'dayjs'

// KYC操作模式枚举
export enum KycMode {
  Add = 'add',       // 新增KYC
  Edit = 'edit',     // 编辑KYC（审核失败时）
  View = 'view',     // 查看KYC（预审核通过时，只读）
}

// KYC流程步骤枚举
export enum KycStep {
  Intro = 'intro',                         // 引导页
  SelectCardholderType = 'cardholder_type', // 选择持卡人身份类型
  BasicInfo = 'basic_info',                // 基础持卡人信息（国家/姓名/证件类型/证件号/邮箱）
  CompleteInfo = 'complete_info',          // 信息完善（性别/手机号/国籍/出生日期/职务/地址等）
  UploadDocument = 'upload',               // 上传证件
  UploadHandheld = 'handheld',             // 上传手持证件照片
  Success = 'success',                     // 提交成功
}

// 持卡人身份类型枚举
export enum CardholderType {
  Mainland = 'mainland',   // 大陆持卡人
  Overseas = 'overseas',   // 海外持卡人
}

/**
 * 产品 ModelsCardProduct.allowed_kyc_types 规则（以此为准）：
 * - 空数组 []：不限制地区，大陆与海外均可选；
 * - 非空：含 "id_china" 或 "id_passport" 之一 → 可选大陆持卡人；含 "id_abroad" 或 "passport_abroad" 之一 → 可选海外持卡人。
 * 大陆组与海外组在产品侧互斥（白名单只描述其中一类）；前端按 token 是否出现分别禁用另一地区。
 * 未传 null/undefined 时与「无白名单」一致，不限制地区（如从非开卡入口进入添加 KYC）。
 */
const ALLOWED_KYC_MAINLAND = new Set(['id_china', 'id_passport'])
const ALLOWED_KYC_OVERSEAS = new Set(['id_abroad', 'passport_abroad'])

/**
 * 接口可能将 allowed_kyc_types 以 JSON 字符串返回（如 "[\"id_china\"]"），统一为 string[]。
 * undefined：未传；[]：明确空数组，与不限制一致。
 */
export function normalizeAllowedKycTypes(raw: unknown): string[] | undefined {
  const toTrimmedStrings = (arr: unknown[]): string[] =>
    arr
      .filter((x): x is string => typeof x === 'string')
      .map((s) => s.trim())
      .filter(Boolean)

  if (raw == null) return undefined
  if (Array.isArray(raw)) {
    return toTrimmedStrings(raw)
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim()
    if (!trimmed) return undefined
    try {
      const parsed = JSON.parse(trimmed) as unknown
      if (Array.isArray(parsed)) {
        return toTrimmedStrings(parsed)
      }
    } catch {
      /* 非 JSON 则忽略 */
    }
  }
  return undefined
}

/** 根据 allowed_kyc_types 计算大陆/海外是否可选，逻辑见上方面向产品的规则说明。 */
export function getAllowedCardholderTypes(allowedKycTypes: string[] | undefined | null): {
  mainland: boolean
  overseas: boolean
  unrestricted: boolean
} {
  if (allowedKycTypes == null || allowedKycTypes.length === 0) {
    return { mainland: true, overseas: true, unrestricted: true }
  }
  const set = new Set(allowedKycTypes)
  const mainland = [...ALLOWED_KYC_MAINLAND].some((k) => set.has(k))
  const overseas = [...ALLOWED_KYC_OVERSEAS].some((k) => set.has(k))
  return { mainland, overseas, unrestricted: false }
}

// 证件类型枚举（已抽离，便于后续扩展新证件类型）
export enum DocumentType {
  IdCard = 'id_card',          // 身份证
  Passport = 'passport',      // 护照
  // ResidentCard = 'resident_card', // 居留证（预留扩展）
}

// 证件类型选项接口
export interface DocumentTypeOption {
  type: DocumentType
  enabled: boolean
}

// 根据持卡人身份类型获取可用的证件类型选项（含启用/禁用状态）
export const getDocumentTypeOptions = (cardholderType: CardholderType): DocumentTypeOption[] => {
  switch (cardholderType) {
    case CardholderType.Mainland:
      return [
        { type: DocumentType.IdCard, enabled: true },
        { type: DocumentType.Passport, enabled: false },
      ]
    case CardholderType.Overseas:
      return [
        { type: DocumentType.IdCard, enabled: false },
        { type: DocumentType.Passport, enabled: true },
      ]
  }
}

// 根据持卡人身份类型获取默认证件类型
export const getDefaultDocumentType = (cardholderType: CardholderType): DocumentType => {
  switch (cardholderType) {
    case CardholderType.Mainland:
      return DocumentType.IdCard
    case CardholderType.Overseas:
      return DocumentType.Passport
  }
}

// KYC表单数据类型 - 对应 ApplyKycRequest
export interface KycFormData {
  // 持卡人身份类型
  cardholder_type: CardholderType
  // 基础信息步骤字段
  address_country: string      // 国家/地区
  first_name: string           // 名
  last_name: string            // 姓
  identity_type: DocumentType  // 证件类型
  identity_number: string      // 证件号码
  email: string                // 电子邮箱
  // 完善信息步骤字段
  sex: string                  // 性别: male | female
  phone_zone_number: string    // 手机区号
  phone: string                // 手机号
  nationality: string          // 国籍
  identity_issue_country: string // ISO国家代码
  birth_date: string           // 出生日期 yyyy-mm-dd
  occupation: string           // 职务
  address_province: string     // 省份
  address_city: string         // 城市
  address: string              // 详细地址
  // 证件图片URL
  idCardFrontUrl: string
  idCardBackUrl: string
  idCardHandheldUrl: string
  passportFrontUrl: string
  passportHandheldUrl: string
  // KYC要素验证状态
  kyc_verified: boolean
}

// 初始表单数据
export const initialFormData: KycFormData = {
  cardholder_type: CardholderType.Mainland,
  address_country: '',
  first_name: '',
  last_name: '',
  identity_type: DocumentType.IdCard,
  identity_number: '',
  email: '',
  sex: '',
  phone_zone_number: '+86',
  phone: '',
  nationality: '',
  identity_issue_country: '',
  birth_date: '',
  occupation: '',
  address_province: '',
  address_city: '',
  address: '',
  idCardFrontUrl: '',
  idCardBackUrl: '',
  idCardHandheldUrl: '',
  passportFrontUrl: '',
  passportHandheldUrl: '',
  kyc_verified: false,
}

/** 开卡场景下根据 allowed_kyc_types 生成初始表单（仅允许一种地区时默认选中该地区）。未传或空数组与不限制一致。 */
export function getInitialKycFormForAllowedTypes(allowedKycTypes?: string[] | null): KycFormData {
  const { mainland, overseas, unrestricted } = getAllowedCardholderTypes(allowedKycTypes)
  if (unrestricted || (mainland && overseas)) {
    return { ...initialFormData }
  }
  if (mainland && !overseas) {
    return {
      ...initialFormData,
      cardholder_type: CardholderType.Mainland,
      identity_type: getDefaultDocumentType(CardholderType.Mainland),
    }
  }
  if (!mainland && overseas) {
    return {
      ...initialFormData,
      cardholder_type: CardholderType.Overseas,
      identity_type: getDefaultDocumentType(CardholderType.Overseas),
    }
  }
  return { ...initialFormData }
}

// 从API响应转换为表单数据（列表页使用 ListKyc 类型）
export const convertApiToFormData = (kyc: ListKyc | undefined): KycFormData => {
  if (!kyc) return initialFormData
  
  const photos = kyc.identity_photos
  const isPassport = kyc.identity_type === 'passport'
  
  return {
    cardholder_type: isPassport ? CardholderType.Overseas : CardholderType.Mainland,
    address_country: kyc.address_country || '',
    first_name: kyc.first_name || '',
    last_name: kyc.last_name || '',
    identity_type: isPassport ? DocumentType.Passport : DocumentType.IdCard,
    identity_number: kyc.identity_number || '',
    email: kyc.email || '',
    sex: kyc.sex || '',
    phone_zone_number: kyc.phone_zone_number || '+86',
    phone: kyc.phone || '',
    nationality: kyc.nationality || '',
    identity_issue_country: kyc.identity_issue_country || '',
    birth_date: kyc.birth_date ? dayjs(kyc.birth_date).format('YYYY-MM-DD') : '',
    occupation: kyc.occupation || '',
    address_province: kyc.address_province || '',
    address_city: kyc.address_city || '',
    address: kyc.address || '',
    idCardFrontUrl: !isPassport ? (photos?.front || '') : '',
    idCardBackUrl: !isPassport ? (photos?.back || '') : '',
    idCardHandheldUrl: !isPassport ? (photos?.handheld || '') : '',
    passportFrontUrl: isPassport ? (photos?.front || '') : '',
    passportHandheldUrl: isPassport ? (photos?.handheld || '') : '',
    kyc_verified: true,
  }
}

// 从KYC详情接口响应转换为表单数据（详情页使用 ModelsKyc 类型）
// 注意：新的 ModelsKyc 字段与 ListKyc 一致
export const convertDetailToFormData = (kyc: ModelsKyc | undefined): KycFormData => {
  if (!kyc) return initialFormData
  
  const photos = kyc.identity_photos
  const isPassport = kyc.identity_type === 'passport'
  
  return {
    cardholder_type: isPassport ? CardholderType.Overseas : CardholderType.Mainland,
    address_country: kyc.address_country || '',
    first_name: kyc.first_name || '',
    last_name: kyc.last_name || '',
    identity_type: isPassport ? DocumentType.Passport : DocumentType.IdCard,
    identity_number: kyc.identity_number || '',
    email: kyc.email || '',
    sex: kyc.sex || '',
    phone_zone_number: kyc.phone_zone_number || '+86',
    phone: kyc.phone || '',
    nationality: kyc.nationality || '',
    identity_issue_country: kyc.identity_issue_country || '',
    birth_date: kyc.birth_date ? dayjs(kyc.birth_date).format('YYYY-MM-DD') : '',
    occupation: kyc.occupation || '',
    address_province: kyc.address_province || '',
    address_city: kyc.address_city || '',
    address: kyc.address || '',
    idCardFrontUrl: !isPassport ? (photos?.front || '') : '',
    idCardBackUrl: !isPassport ? (photos?.back || '') : '',
    idCardHandheldUrl: !isPassport ? (photos?.handheld || '') : '',
    passportFrontUrl: isPassport ? (photos?.front || '') : '',
    passportHandheldUrl: isPassport ? (photos?.handheld || '') : '',
    kyc_verified: true,
  }
}

// 邮箱验证正则
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// 手机号验证正则（6-15位数字）
const PHONE_REGEX = /^\d{6,15}$/

// 检查出生日期是否表示已年满18岁
export const isAtLeast18YearsOld = (birthDate: string): boolean => {
  if (!birthDate?.trim()) return false
  const birth = dayjs(birthDate.trim())
  if (!birth.isValid()) return false
  const today = dayjs().startOf('day')
  const age18Date = birth.add(18, 'year')
  return today.isSame(age18Date, 'day') || today.isAfter(age18Date, 'day')
}

// 检查基础信息是否完整有效（StepBasicInfo步骤）
export const isBasicInfoValid = (formData: KycFormData): boolean => {
  return !!(
    formData.address_country?.trim() &&
    formData.first_name?.trim() &&
    formData.last_name?.trim() &&
    formData.identity_type &&
    formData.identity_number?.trim() &&
    formData.email?.trim() &&
    EMAIL_REGEX.test(formData.email)
  )
}

// 检查完善信息是否完整有效（StepCompleteInfo步骤）
export const isCompleteInfoValid = (formData: KycFormData): boolean => {
  return !!(
    formData.sex &&
    formData.phone?.trim() &&
    PHONE_REGEX.test(formData.phone) &&
    formData.nationality?.trim() &&
    formData.birth_date &&
    isAtLeast18YearsOld(formData.birth_date) &&
    formData.occupation &&
    formData.address_city?.trim() &&
    formData.address?.trim()
  )
}

// 检查全部个人信息是否完整有效（兼容EditKyc等场景）
export const isPersonalInfoValid = (formData: KycFormData): boolean => {
  return isBasicInfoValid(formData) && isCompleteInfoValid(formData)
}

// 检查证件上传是否完整
export const isDocumentUploadValid = (formData: KycFormData): boolean => {
  const isIdCard = formData.identity_type === DocumentType.IdCard
  
  if (isIdCard) {
    return !!(formData.idCardFrontUrl && formData.idCardBackUrl)
  } else {
    return !!formData.passportFrontUrl
  }
}

// 检查手持证件照片是否已上传
export const isHandheldUploadValid = (formData: KycFormData): boolean => {
  const isIdCard = formData.identity_type === DocumentType.IdCard
  
  if (isIdCard) {
    return !!formData.idCardHandheldUrl
  } else {
    return !!formData.passportHandheldUrl
  }
}

// 检查所有表单是否有效（用于编辑页面）
export const isAllFormValid = (formData: KycFormData): boolean => {
  return isPersonalInfoValid(formData) && isDocumentUploadValid(formData) && isHandheldUploadValid(formData)
}

// 将表单数据转换为API请求参数
export const convertFormDataToApiRequest = (formData: KycFormData): ApplyKycRequest => {
  const isIdCard = formData.identity_type === DocumentType.IdCard
  
  return {
    address: formData.address,
    address_city: formData.address_city,
    address_country: formData.address_country,
    address_province: formData.address_province,
    birth_date: formData.birth_date,
    email: formData.email,
    first_name: formData.first_name,
    identity_issue_country: formData.identity_issue_country,
    identity_number: formData.identity_number,
    identity_photos: {
      front: isIdCard ? formData.idCardFrontUrl : formData.passportFrontUrl,
      back: isIdCard ? formData.idCardBackUrl : undefined,
      handheld: isIdCard ? formData.idCardHandheldUrl : formData.passportHandheldUrl,
    },
    identity_type: formData.identity_type as unknown as IdentityType,
    last_name: formData.last_name,
    nationality: formData.nationality,
    occupation: formData.occupation,
    phone: formData.phone,
    phone_zone_number: formData.phone_zone_number,
    sex: formData.sex as Sex,
    setprimary: true,
  }
}

